import fetch from "cross-fetch";
import { useQuery } from "react-query";
import settings from "src/settings";
import { PlayoffsEntry } from "./useSession";

const { API_BACKEND_HOSTNAME } = settings;

/**
 * Hook that uses react-query to interface with the Game API store (for game.json)
 * @example
 * import useGame from "@hooks/useGame";
 * ...
 * const { data: game } = useGame();
 * const drivers = game.drivers
 */
export default function useGame() {
  return useQuery<IGame, Error>(
    "game",
    () =>
      fetch(`${API_BACKEND_HOSTNAME}/api/v1/chase/game.json`, {
        headers: { "content-type": "application/json" },
        method: "GET",
      }).then((res) => res.json()),
    {
      refetchInterval: 30 * 1000,
      refetchOnWindowFocus: false,
      initialData: {
        rounds: [
          {
            round_id: 1,
            name: "Round of 16",
            slots: 12,
          },
          {
            round_id: 2,
            name: "Round of 12",
            slots: 8,
          },
          {
            round_id: 3,
            name: "Round of 8",
            slots: 4,
          },
          {
            round_id: 4,
            name: "Championship 4",
            slots: 1,
          },
        ],
      },
    },
  );
}

export interface IGame {
  announcements?: IAnnouncment[];
  gigya_key?: string;
  state?: {
    current_round: number;
    current_race_index: number;
  };
  drivers?: IDriver[];
  races?: {
    [raceId: string]: IRace;
  };
  rounds: IRound[];
  results?: {
    rounds: IResultRound[];
  };
  predictions?: {
    rounds: IRoundPrediction[];
  };
  featured_entry?: PlayoffsEntry;
}

export interface IDriver {
  driver_id: number;
  name: string;
  first_name: string;
  last_name: string;
  full_name: string;
  car_number: string;
  crew_chief: string;
  hometown: string;
  team_name: string;
  manufacturer: string;
  headshot: string;
  car_number_class: string;
  badge: string;
  percent_owned: string;
  track_stats: ITrackStat[];
  odds: number[];
}

interface ITrackStat {
  track_name: string;
  starts: number;
  poles: number;
  top10: number;
  top5: number;
  wins: number;
}

interface IRace {
  race_name: string;
  race_date: number;
  track_name: string;
  image_path: string;
  television_broadcaster: string;
  radio_broadcaster: string;
  is_scoring_run: boolean;
}

interface IRound {
  round_id: number;
  name: string;
  slots: number;
  starts_at?: number;
  locks_at?: number;
  races?: number[];
  scoring_status?: "pre" | "final" | "ready" | "running";
}

interface IResultRound {
  round_id: number;
  races: {
    race_id: number;
    drivers: number[];
  }[];
  drivers: number[];
}

interface IRoundPrediction {
  round_id: number;
  drivers: number[];
}

interface IAnnouncment {
  id: number;
  message: string;
  starts_at: number;
  ends_at: number;
}

export function getDriver(driverId: number, game: IGame): IDriver {
  return game?.drivers?.find((driver) => driver.driver_id === driverId);
}
export function getRace(raceId: number, game: IGame): IRace {
  return game?.races?.[raceId];
}
